/**
 * InvestorOverviewPage — VC-caliber one-pager (iter 86).
 *
 * Route: /overview (token-gated by InvestorOverviewGate.js)
 *
 * Designed per user spec — scannable, premium, hero=WAVE Deployment Tech.
 * Design system locked:
 *   bg       #0A0A0A
 *   card     #141414  (architecture cards, etc.)
 *   dict     #1F1F1F  (slightly raised dictionary card)
 *   teal     #00D4FF
 *   pink     #FF4D94
 *   Inter font, 8pt grid, 12px card radius, subtle glows on hover/active.
 *
 * Layout (top → bottom):
 *   1. Top bar (logo + date)
 *   2. Centered hero thesis
 *   3. WaveDictionaryEntry (kept — already perfect)
 *   4. "Core Architecture · Already Built & Live" — 4 clickable cards
 *   5. Dynamic content area (defaults to Adaptive Burst Orchestration)
 *   6. Complementary, not competitive (text box)
 *   7. Strong closing + Live Demo CTA
 *   8. Founder footer
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, Sparkles, Zap, ArrowRight,
  CheckCircle2, MapPin, Rocket, Mail, Activity,
  TrendingUp, PenTool, LifeBuoy, Calendar, MessageCircle,
  Video, BarChart3, Briefcase, Workflow, ChevronDown,
} from 'lucide-react';
import WaveDictionaryEntry from '../components/investor/WaveDictionaryEntry';
import { StatusChip } from '../components/investor/overview/primitives';
import { FiringOrder } from '../components/investor/overview/FiringOrder';

const TEAL = '#00D4FF';
const PINK = '#FF4D94';
const PURPLE = '#A78BFA';        // CMD accent — Invention 9
const PURPLE_DEEP = '#6D4DEC';
const BG = '#0A0A0A';
const CARD = '#141414';

// ───────────────────────────────────────────────────────────────────────
// Logo mark — gradient stroke wave, inline SVG.
// ───────────────────────────────────────────────────────────────────────
function WaveLogoMark({ size = 26, animated = false }) {
  const id = `wlm-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={TEAL} />
          <stop offset="100%" stopColor={PINK} />
        </linearGradient>
      </defs>
      <path d="M8 38 C 16 28, 24 28, 32 38 S 48 48, 56 38" stroke={`url(#${id})`} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M8 28 C 16 18, 24 18, 32 28 S 48 38, 56 28" stroke={`url(#${id})`} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.65" />
      <path d="M8 18 C 16 12, 24 12, 32 18 S 48 24, 56 18" stroke={`url(#${id})`} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4">
        {animated && <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3s" repeatCount="indefinite" />}
      </path>
    </svg>
  );
}

// ───────────────────────────────────────────────────────────────────────
// 5 architecture pillars — DSP and SSP anchor the row; the middle tablet
// holds three stacked sub-pillars (Digital DNA · CMD · Wave Deployment
// Tech) which read together as the "power trio" of authentic algorithmic
// growth. Keys ordered so WAVE Deployment Tech remains LAST and is the
// default-selected card.
// ───────────────────────────────────────────────────────────────────────
const PILLARS = [
  {
    key:    'dsp',
    label:  'DSP',
    sub:    'Demand-Side Platform',
    icon:   Building2,
    color:  TEAL,
    blurb:  'Where brands deploy waves',
  },
  {
    key:    'ssp',
    label:  'SSP',
    sub:    'Supply-Side Platform',
    icon:   Users,
    color:  PINK,
    blurb:  'Where everyday creators get paid',
  },
  {
    key:    'dna',
    label:  'Digital DNA',
    sub:    'Semantic matching',
    icon:   Sparkles,
    color:  TEAL,
    blurb:  'Zero-party data · one shared meaning-space',
    status: ['built'],
  },
  {
    // Cohort Mapping — lives between DNA and the Orchestrator in the middle
    // tablet because that's the actual data flow: DNA matches → Cohort
    // Mapping groups + sequences → Orchestrator deploys.
    key:    'cmd',
    label:  'Cohort Mapping',
    sub:    'Audience overlap · whitespace',
    icon:   Sparkles,
    color:  PURPLE,
    blurb:  'The frontier the engine is built toward',
    status: ['developing'],
  },
  {
    key:    'tech',
    label:  'Adaptive Burst Orchestration',
    sub:    'Squads, angles, roll-out roles, timing',
    icon:   Zap,
    color:  PINK,
    blurb:  'The choreography layer',
    status: ['built', 'filed'],
    hero:   true,
  },
];

// ───────────────────────────────────────────────────────────────────────
// Middle tablet group — DSP and SSP stand alone; the middle tablet
// bundles the two intelligence products (Digital DNA + Wave Deployment
// Tech) as separate clickable sub-tiles under one header. Keeps the
// 3-tablet symmetry of "demand · intelligence · supply" without
// inflating the architecture to four equal-weight pillars.
// ───────────────────────────────────────────────────────────────────────
function MiddleTabletGroup({ pillars, active, onSelect }) {
  return (
    <div
      className="rounded-xl p-3 lg:p-4 flex flex-col"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px dashed rgba(255,255,255,0.08)',
      }}
      data-testid="pillar-middle-group"
    >
      <p className="text-[9px] uppercase tracking-[0.26em] font-black text-gray-500 mb-2 px-1">
        Targeting & Intelligence
      </p>
      {/* The three sub-tablets each flex to share available height so the
          middle column reads with the same visual weight as DSP / SSP.
          DNA · CMD · Tech — the data flow path that matches creators,
          predicts how they amplify, and deploys the burst sequence. */}
      <div className="flex flex-col gap-2 flex-1">
        {pillars.map(p => (
          <div key={p.key} className="flex-1 min-h-0">
            <PillarCard
              pillar={p}
              active={p.key === active}
              onSelect={onSelect}
              compact
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Architecture pillar card
// • Full mode (DSP / SSP) — big monogram title + Unit economics block +
//   one-line revenue model. Designed to fill the height of the middle
//   tablet group which holds two stacked sub-tiles.
// • Compact mode (DNA / Wave Deployment Tech inside middle group) —
//   tight inline tile, no economics block.
// ───────────────────────────────────────────────────────────────────────
function PillarCard({ pillar, active, onSelect, compact = false }) {
  const Icon = pillar.icon;
  const isMonogram = !compact && pillar.label && pillar.label.length <= 4;
  return (
    <button
      type="button"
      data-testid={`pillar-card-${pillar.key}`}
      onClick={() => onSelect(pillar.key)}
      className={`group relative text-left rounded-xl transition-all duration-300 hover:-translate-y-0.5 w-full h-full flex flex-col ${
        compact ? 'p-3 lg:p-3.5' : 'p-5 lg:p-6'
      }`}
      style={{
        background: CARD,
        border: active ? `1px solid ${pillar.color}` : '1px solid rgba(255,255,255,0.06)',
        boxShadow: active
          ? `0 0 38px ${pillar.color}22, inset 0 0 0 1px ${pillar.color}33`
          : '0 0 0 transparent',
      }}
    >
      {/* hero glow corner (only on WAVE Deployment Tech) */}
      {pillar.hero && !compact && (
        <div
          className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-30 pointer-events-none transition-opacity duration-300 group-hover:opacity-50"
          style={{ background: pillar.color }}
        />
      )}

      <div className="relative flex-1 flex flex-col">
        {/* Top row: icon + optional hero pill */}
        <div className="flex items-center gap-2 mb-2">
          <Icon
            className={`transition-transform duration-300 group-hover:scale-110 ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`}
            style={{ color: pillar.color }}
          />
          {pillar.hero && (
            <span
              className="text-[8px] uppercase tracking-[0.22em] font-black px-1.5 py-0.5 rounded"
              style={{ background: `${pillar.color}1a`, color: pillar.color, border: `1px solid ${pillar.color}33` }}
              data-testid="pillar-hero-tag"
            >
              Core engine
            </span>
          )}
        </div>

        {/* Built / Developing / filing status — only stated where true */}
        {pillar.status && (
          <div className="flex flex-wrap items-center gap-1 mb-2" data-testid={`pillar-status-${pillar.key}`}>
            {pillar.status.map(s => <StatusChip key={s} status={s} />)}
          </div>
        )}

        {/* Title — monogram treatment for 3-letter labels like DSP/SSP */}
        {isMonogram ? (
          <>
            <h3
              className="font-black text-white tracking-tight leading-none"
              style={{
                fontSize: 'clamp(40px, 6.5vw, 64px)',
                letterSpacing: '-0.04em',
              }}
            >
              {pillar.label}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-400 mt-2">
              {pillar.sub}
            </p>
            <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">{pillar.blurb}</p>
          </>
        ) : (
          <>
            <h3 className={`font-black text-white tracking-tight leading-snug ${compact ? 'text-[13px]' : 'text-base lg:text-lg'}`}>
              {pillar.label}
            </h3>
            <p className={`text-gray-500 leading-snug ${compact ? 'text-[10px] mt-0.5' : 'text-[11px] mt-1'}`}>{pillar.sub}</p>
            {!compact && (
              <p className="text-[10px] text-gray-600 mt-3 leading-snug">{pillar.blurb}</p>
            )}
          </>
        )}

        {/* Unit economics block — DSP / SSP only */}
        {pillar.econ && (
          <div
            className="mt-4 pt-3 space-y-1.5"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            data-testid={`pillar-econ-${pillar.key}`}
          >
            <p
              className="text-[9px] uppercase tracking-[0.22em] font-black"
              style={{ color: pillar.color }}
            >
              Unit economics
            </p>
            {pillar.econ.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-2 text-[11px] leading-tight">
                <span className="text-gray-500 shrink-0">{k}</span>
                <span className="text-white font-semibold text-right">{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Revenue model one-liner */}
        {pillar.revenue && (
          <div
            className="mt-3 pt-2.5"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            data-testid={`pillar-revenue-${pillar.key}`}
          >
            <p
              className="text-[9px] uppercase tracking-[0.22em] font-black mb-1"
              style={{ color: pillar.color }}
            >
              Revenue model
            </p>
            <p className="text-[11px] text-gray-300 leading-snug">{pillar.revenue}</p>
          </div>
        )}

        {/* Bottom CTA — pinned to base */}
        <div
          className={`flex items-center gap-1 uppercase tracking-wider font-bold transition-colors mt-auto ${
            compact ? 'pt-2 text-[9px]' : 'pt-4 text-[10px]'
          }`}
          style={{ color: active ? pillar.color : '#4b5563' }}
        >
          {active ? 'Viewing' : 'View details'}
          <ArrowRight className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        </div>
      </div>
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Content blocks per pillar
// ───────────────────────────────────────────────────────────────────────
function ContentShell({ title, kicker, accent = TEAL, children }) {
  return (
    <section
      className="rounded-xl p-6 lg:p-8 relative overflow-hidden"
      style={{
        background: CARD,
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: `0 0 60px ${accent}10`,
      }}
      data-testid="pillar-content-shell"
    >
      <div
        className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: accent }}
      />
      <div className="relative">
        {kicker && (
          <p className="text-[10px] uppercase tracking-[0.28em] font-black mb-2" style={{ color: accent }}>
            {kicker}
          </p>
        )}
        <h2 className="text-2xl lg:text-3xl font-black text-white tracking-tight leading-tight mb-6">
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

// 1) Adaptive Burst Orchestration ─────────────────────────────────────
// Built capabilities in the present tense; reacceleration is labeled
// developing (wave-engine loop is disabled in Phase 0).
function TechContent() {
  const tiles = [
    { label: 'Multi-burst scheduling', note: 'Staggered, jittered post windows per burst', status: 'built' },
    { label: 'Angle diversity',        note: 'Five creative angles dealt so no burst repeats the same ad', status: 'built' },
    { label: 'Adaptive reacceleration', note: 'Shifts later bursts on live signal — not yet operating', status: 'developing' },
  ];
  return (
    <ContentShell title="The choreography layer." kicker="Adaptive Burst Orchestration · Provisional filed" accent={PINK}>
      <p className="text-base text-gray-300 leading-relaxed max-w-3xl mb-6">
        Takes the cohort Digital DNA matched, composes it into small squads, gives every micro-creator{' '}
        <span className="text-white font-bold">one creative angle and one roll-out role</span>, and schedules the bursts
        so the wave reads as culture, not as a campaign. Plans are computed server-side and{' '}
        <span className="text-white font-bold">frozen at send</span>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {tiles.map(f => (
          <div key={f.label} className="rounded-lg p-4 bg-black/40 border border-white/5" data-testid={`orch-tile-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: f.status === 'built' ? PINK : '#FFC83D' }} />
                <p className="text-xs font-bold text-white">{f.label}</p>
              </div>
              <StatusChip status={f.status} />
            </div>
            <p className="text-[10px] text-gray-500 leading-snug">{f.note}</p>
          </div>
        ))}
      </div>
      <div className="mb-6"><FiringOrder /></div>
      <div className="rounded-lg p-4 lg:p-5"
           style={{ background: `${PINK}08`, border: `1px solid ${PINK}33` }}>
        <p className="text-[11px] uppercase tracking-[0.22em] font-black mb-1" style={{ color: PINK }}>
          The Result
        </p>
        <p className="text-base text-white leading-snug">
          Coordinated timing of <span className="text-white font-bold">authentic, disclosed, creator-made content</span>
          <span className="text-gray-400"> — many small, trusted audiences reached at once. A wave is a lattice of small squads,
          not one monolithic campaign, so the same choreography runs a handful of micro-creators or several hundred.</span>
        </p>
      </div>
    </ContentShell>
  );
}

// 1b) Creator Manifold Dynamics (Invention 9) ───────────────────────────
// Iter 86.6 — Founder feedback: "make this more understandable &
// digestable". The previous version had two prose paragraphs saying
// similar things, plus an abstract 3-tile row (Patches / Simulation /
// Optimization), plus a strategy sentence about Series A. Too much.
//
// This rewrite leans hard on a single scan-able 3-step flow with
// arrows — Wire → Sequence → Fire — and a one-sentence opener that
// anchors CMD's position in the pipeline ("between matching and
// deploying"). The moat shrinks to one punch line.
//
// Iter 86.8 (founder pass):
//   • Renamed Cohort → Creator Manifold Dynamics across this page.
//   • Swapped step labels Cluster/Simulate/Sequence → Wire/Sequence/Fire.
//   • Removed the "Six lenses · one cohort" section — it crowded the page
//     and slowed the read; the moat punch line carries the close.
//   • Centered the step-box content.
function CmdContent() {
  const steps = [
    {
      label: 'Wire',
      body:  'Group micro-creators into small squads based on who they reach and how much their audiences overlap. Runs today.',
    },
    {
      label: 'Sequence',
      body:  'Forecast how one squad’s post lifts the next from measured cross-engagement and audience-overlap data. Developing — not yet operating.',
    },
    {
      label: 'Fire',
      body:  'Hand the chosen order to Burst Orchestration. Launch the wave.',
    },
  ];
  return (
    <ContentShell
      title="The intelligence layer between matching and deploying."
      kicker="Cohort Mapping"
      accent={PURPLE}
    >
      {/* One-sentence anchor — where Cohort Mapping sits in the pipeline. */}
      <p className="text-base text-gray-300 leading-relaxed max-w-3xl mb-7">
        Digital DNA picks the <span className="text-white font-bold">right</span>{' '}
        micro-creators. Cohort Mapping maps{' '}
        <span className="font-bold" style={{ color: PURPLE }}>
          which audiences overlap, where competitors are active or dark, and where the whitespace is
        </span>
        {' '}— so each squad covers the widest unique surface.
      </p>

      {/* 3-step horizontal flow with arrows. Each step is a clearly-labeled
          card with a 1-line concrete description. Scans top-to-bottom on
          mobile, left-to-right on desktop. Centered content per iter-86.8
          so the boxes read as identical-weight pillars (not left-justified
          paragraphs). */}
      <div className="mb-7">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <div
                className="rounded-lg p-4 flex flex-col items-center text-center"
                style={{
                  background: `${PURPLE}10`,
                  border: `1px solid ${PURPLE}33`,
                }}
              >
                <div className="flex items-baseline gap-2 mb-2 justify-center">
                  <span className="text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded"
                        style={{ background: `${PURPLE}22`, color: PURPLE }}>
                    {`0${i + 1}`}
                  </span>
                  <p className="text-base font-black text-white tracking-tight">
                    {s.label}
                  </p>
                </div>
                <p className="text-[11.5px] text-gray-400 leading-relaxed">
                  {s.body}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex items-center justify-center px-1"
                     style={{ color: PURPLE }} aria-hidden="true">
                  <svg width="22" height="14" viewBox="0 0 22 14" fill="none">
                    <path d="M0 7H20M14 1L20 7L14 13" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Moat — single punch line. */}
      <div className="rounded-lg p-4 lg:p-5"
           style={{ background: `${PURPLE}08`, border: `1px solid ${PURPLE}33` }}>
        <p className="text-[11px] uppercase tracking-[0.22em] font-black mb-1" style={{ color: PURPLE }}>
          The Moat
        </p>
        <p className="text-base text-white leading-snug">
          Most platforms match creators. WAVE matches micro-creators,
          <span className="text-white font-bold"> then choreographs how they fire together.</span>
          <span className="text-gray-400">
            {' '}Every plan is frozen at send and paired with what actually happened — the corpus that is designed to sharpen each wave that follows. Deterministic overlap math runs today; the predictive layer is not yet operating.
          </span>
        </p>
      </div>
    </ContentShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// 2) DSP — Restored to the richer iter-78 narrative per user feedback (iter 86.2):
//    Brand-use-case copy with founder voice + bullet highlights + typical
//    spend line + revenue model + unit econ + projections. Two-tab toggle
//    (Small Business / Enterprise) kept.
// ───────────────────────────────────────────────────────────────────────
const DSP_SEGMENTS = {
  smb: {
    label: 'Small Business',
    sub:   'Local · Regional · DTC',
    heroPill: 'Everyday Micro-Creators · Everyday Businesses · Everyday Prices',
    accent: TEAL,
    accentRgb: '0,212,255',
    icon:  MapPin,
    title: 'Build a wave for the price of a single boosted post.',
    body:  "A local med-spa, a Shopify candle brand, a 5-store coffee chain. Spin up a wave of 10–30 nano and micro-creators within radius of every storefront, fully managed by AI, fully measurable, in under three minutes.",
    highlights: [
      'Self-serve Wave Builder — budget, brief, send',
      'Auto-matched cohort using Digital DNA',
      'Auto-generated brief, hook, caption, post stagger',
      'Walk-ins, RSVPs, reach, review lift — closed at the storefront',
    ],
    typicalSpend: '$500 – $5,000 per wave',
    projections: [
      ['Year 1', '8,000 users',  '$15M – $18M'],
      ['Year 2', '35,000 users', '$65M – $80M'],
      ['Year 3', '90,000 users', '$180M – $220M'],
    ],
    assumptions: '~1 wave per quarter per user × $3,500 average spend × 15% take rate (+ subscription)',
  },
  ent: {
    label: 'Enterprise',
    sub:   'National · Holding co · Agency-led',
    heroPill: 'Tentpole Waves · Cultural Moments',
    accent: PINK,
    accentRgb: '255,77,148',
    icon:  Rocket,
    title: 'Build a wave the way you book CTV — per tentpole.',
    body:  "A holding-co sub-brand, a national CPG, a retail-partnered product launch. Coordinate 60–250 micro-creators across TikTok, Instagram and YouTube into a synchronized burst that owns the tentpole moment — Super Bowl, album drop, Black Friday, retail set reset.",
    highlights: [
      'Multi-brand portfolio · agency seat · DNA-matched cohorts',
      'Synchronized burst windows compress reach into hours',
      'Cohort Mapping shows audience overlap and whitespace before send',
      'Closes attribution at retail velocity lift, not impressions',
    ],
    typicalSpend: '$50K – $500K per wave',
    projections: [
      ['Year 1', '12 brands · via agencies', '$8M – $10M'],
      ['Year 2', '35 brands',                '$25M – $35M'],
      ['Year 3', '70 brands',                '$60M – $80M'],
    ],
    assumptions: '~1.5 tentpole waves per month per brand × ~$300K average spend × 15% take rate',
  },
};

function DspContent() {
  const [seg, setSeg] = useState('smb');
  const s = DSP_SEGMENTS[seg];
  return (
    <ContentShell title="Brands build waves." kicker="DSP · Brand side" accent={TEAL}>
      <p className="text-sm text-gray-400 leading-relaxed max-w-3xl mb-6">
        Two markets, one platform. Self-serve for small business, agency-led for enterprise. Tap a tab to flex the opportunity.
      </p>

      {/* Toggle pills */}
      <div
        className="inline-flex p-1 rounded-full mb-6"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.05)' }}
        data-testid="dsp-segment-toggle"
      >
        {Object.entries(DSP_SEGMENTS).map(([key, v]) => {
          const active = seg === key;
          return (
            <button key={key}
                    type="button"
                    onClick={() => setSeg(key)}
                    data-testid={`dsp-segment-${key}`}
                    className="px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition-all duration-300"
                    style={{
                      background: active ? v.accent : 'transparent',
                      color: active ? '#0A0A0A' : '#6b7280',
                      boxShadow: active ? `0 0 16px ${v.accent}55` : 'none',
                    }}>
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Use Case block */}
      <div
        key={`dsp-${seg}`}
        className="rounded-xl p-6 lg:p-7 mb-5"
        style={{
          background: '#0F0F0F',
          border: `1px solid rgba(${s.accentRgb}, 0.30)`,
          animation: 'mc-fade-in 320ms ease-out',
        }}
        data-testid={`dsp-usecase-${seg}`}
      >
        <p className="text-[10px] uppercase tracking-[0.25em] font-black mb-3" style={{ color: s.accent }}>
          Use Case · {s.label}
        </p>
        <p className="text-[11px] text-gray-500 mb-3 italic">{s.heroPill}</p>
        <h4 className="text-lg lg:text-xl font-black text-white leading-tight">{s.title}</h4>
        <p className="text-sm text-gray-300 leading-[1.7] mt-3 max-w-3xl">{s.body}</p>
        <ul className="mt-4 space-y-2">
          {s.highlights.map((h) => (
            <li key={h} className="flex gap-2.5 text-xs text-gray-300 leading-[1.55]">
              <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: s.accent }} />
              <span>{h}</span>
            </li>
          ))}
        </ul>
        <p className="text-[10px] uppercase tracking-[0.22em] font-black mt-5 pt-4 border-t border-white/5"
           style={{ color: s.accent }}>
          Target order size: <span className="text-white">{s.typicalSpend}</span>
          <span className="block normal-case tracking-normal text-[10px] text-gray-600 font-medium mt-1">Design assumption for this market, not an observed average.</span>
        </p>
      </div>

      {/* Revenue + Unit Econ + Projections */}
      <div className="rounded-xl p-6 lg:p-7"
           style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-black mb-2" style={{ color: TEAL }}>
              Revenue Model
            </p>
            <p className="text-xs text-gray-300 leading-[1.6]" data-testid="dsp-take-rate">
              Subscription tiers (<span className="text-white font-bold">$99 / $399 / Custom</span>) + a <span className="text-white font-bold">15% platform fee</span> on every wave budget at escrow release; creators receive the remaining <span className="text-white font-bold">85%</span>. One fee — the creator-side view on the SSP tab is the same 15%, not a second line.
            </p>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black mt-4 mb-1.5" style={{ color: '#4ade80' }}>
              Per-wave contribution (modeled)
            </p>
            <p className="text-xs text-gray-300 leading-[1.6]" data-testid="dsp-unit-econ">
              A <span className="text-white font-bold">$3,500</span> wave carries a <span className="text-white font-bold">$525</span> fee; LLM/cloud ≈ $15 and payment processing ≈ $16 leave <span className="text-white font-bold">≈ $494</span>. Founder operating time excluded — the first waves are founder-operated.
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-black mb-1 text-gray-400">
              Revenue Projections · {s.label}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: '#FFC83D' }} data-testid={`dsp-projection-label-${seg}`}>
              Illustrative model, not a forecast
            </p>
            <div className="space-y-2">
              {s.projections.map(([year, users, rev]) => (
                <div key={year}
                     className="rounded-lg px-4 py-3 flex items-center justify-between gap-3"
                     style={{ background: CARD, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-gray-500">{year}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{users}</p>
                  </div>
                  <p className="text-base font-black tabular-nums" style={{ color: s.accent }}>{rev}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-3 leading-snug" data-testid={`dsp-projection-assumptions-${seg}`}>
              Driving assumptions: {s.assumptions}.
            </p>
          </div>
        </div>
      </div>
      <style>{`@keyframes mc-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </ContentShell>
  );
}

// 3) SSP — Creator economy ─────────────────────────────────────────────
// 9 functionality highlights per iter 86.2 user feedback (screenshot).
// Each tile has icon · title · 1-line description · pink left accent.
const SSP_FEATURES = [
  { title: 'Jump on Trends Seamlessly',  icon: TrendingUp,   blurb: 'Auto-detected trend alerts so creators ride the wave before it crests — never after.' },
  { title: 'Script Generation',           icon: PenTool,      blurb: 'AI-generated hooks, scripts, and captions tuned to each creator’s voice and niche.' },
  { title: '24/7 Biz Support & Service',  icon: LifeBuoy,     blurb: 'On-demand help: tax tips, payout questions, brand disputes, content reviews, anytime.' },
  { title: 'Timed Briefs & Posting',       icon: Calendar,     blurb: 'Notify-and-post: every creator gets a timed brief and posts natively, by their own hand, inside the burst window.' },
  { title: 'Community Engagement',        icon: MessageCircle,blurb: 'Built-in DM, comment-triage, and follower-engagement tools that compound social proof.' },
  { title: 'Studio Features',             icon: Video,        blurb: 'In-app trim, caption, color, and music library — full production studio, no third-party app.' },
  { title: 'Reporting Suite',             icon: BarChart3,    blurb: 'Real-time dashboards for reach, engagement, follower growth, and weekly Stripe earnings.' },
  { title: 'Direct Access to Brands',     icon: Briefcase,    blurb: 'Wave Inbox shows every RFP the creator is DNA-qualified for — no agent, no spam.' },
  { title: 'Simplified Deal Flow',        icon: Workflow,     blurb: 'One-tap accept → auto brief → upload → payout. Zero contracts. Zero invoicing.' },
];

function SspContent() {
  return (
    <ContentShell title="Supply-Side Platform · The Creator Economy Engine" kicker="SSP" accent={PINK}>
      <p className="text-base text-gray-300 leading-relaxed max-w-3xl mb-6">
        WAVE turns casual micro-creators into <span className="text-white font-bold">paid, consistent content creators</span> — direct brand access, effortless monetization, and real earnings, with <span className="text-white font-bold">no upfront cost</span>.
      </p>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] font-black mb-3" style={{ color: PINK }}>
          What unlocks the supply of micro-creators
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-6" data-testid="ssp-features-grid">
          {SSP_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title}
                   className="relative rounded-lg p-4 bg-black/40 transition-colors hover:bg-black/55"
                   style={{ borderLeft: `2px solid ${PINK}`, border: '1px solid rgba(255,255,255,0.05)', borderLeftColor: PINK, borderLeftWidth: '2px' }}
                   data-testid={`ssp-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: PINK }} />
                  <p className="text-xs font-bold text-white tracking-tight">{f.title}</p>
                </div>
                <p className="text-[11px] text-gray-400 leading-snug">{f.blurb}</p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-lg p-4 mb-6"
           style={{ background: `${PINK}08`, border: `1px solid ${PINK}33` }}>
        <p className="text-[10px] uppercase tracking-[0.22em] font-black mb-1" style={{ color: PINK }}>Revenue Model</p>
        <p className="text-base text-white" data-testid="ssp-take-rate">
          Creators keep <span className="font-black">85%</span> of every wave budget. The <span className="font-black">15% platform fee</span> is the same fee shown on the DSP tab — one take rate, not a second revenue line. No creator subscriptions.
        </p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] font-black mb-1" style={{ color: PINK }}>
          Growth Projections
        </p>
        <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: '#FFC83D' }} data-testid="ssp-projection-label">
          Illustrative model, not a forecast
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            ['Year 1', '30,000 active creators',  '$4M – $6M'],
            ['Year 2', '120,000 active creators', '$18M – $22M'],
            ['Year 3', '300,000 active creators', '$45M – $55M'],
          ].map(([year, scale, rev]) => (
            <div key={year} className="rounded-lg p-4 bg-black/40 border border-white/5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{year}</p>
              <p className="text-sm text-white font-bold mt-0.5">{scale}</p>
              <p className="text-xl font-black tabular-nums mt-2" style={{ color: PINK }}>{rev}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-500 mt-3 leading-snug" data-testid="ssp-projection-assumptions">
          Driving assumptions: active creators × ~$1,100 average creator earnings per year × 15% take rate. Same fee as the DSP view — not additive to the DSP tables.
        </p>
      </div>
    </ContentShell>
  );
}

// 4) Digital DNA ─────────────────────────────────────────────
function DnaContent() {
  return (
    <ContentShell title="Proprietary Matching · Built on Zero-Party Data" kicker="Digital DNA" accent={TEAL}>
      <p className="text-base text-gray-300 leading-relaxed max-w-3xl mb-6">
        Proprietary matching engine built on <span className="text-white font-bold">zero-party data</span>. Brands and micro-creators live in one shared meaning-space, so “the creators my buyers actually listen to” is a computation, not a guess.{' '}
        <span className="text-gray-400">
          Cohort selection <span className="text-white font-bold">penalizes audience overlap</span> so every squad reaches the widest unique surface, not just the highest-ranked individuals. Designed to recalibrate as the wave corpus accrues.
        </span>
      </p>
      <div className="rounded-lg p-5"
           style={{ background: `${TEAL}08`, border: `1px solid ${TEAL}33` }}>
        <p className="text-[10px] uppercase tracking-[0.22em] font-black mb-2" style={{ color: TEAL }}>Impact</p>
        <p className="text-base text-white leading-relaxed">
          <span className="font-bold">Fit over follower count.</span> Match quality and creator acceptance are the design objectives; measured lift is reported once the first waves complete.
        </p>
      </div>
    </ContentShell>
  );
}

// 4b) Complementary, not competitive — text box in the same shell style.
function ComplementBox() {
  return (
    <ContentShell title="Waves make the talent brands already pay for work harder." kicker="Complementary, not competitive" accent={TEAL}>
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4" data-testid="complement-box">
        <p className="text-base text-gray-300 leading-relaxed max-w-3xl">
          Most brands already fund a macro-creator or two per campaign. A wave does not replace them — it surrounds them.
          When a cohort of micro-creators fires around the macro-creator&apos;s post, that post becomes{' '}
          <span className="text-white font-bold">the peak of a visible swell</span> rather than a single spike that fades alone.
        </p>
        <div className="rounded-lg p-4 bg-black/40 border border-white/5" data-testid="complement-investor-angle">
          <p className="text-[10px] uppercase tracking-[0.22em] font-black mb-2" style={{ color: TEAL }}>Why it matters commercially</p>
          <p className="text-sm text-gray-300 leading-[1.65]">
            WAVE is <span className="text-white font-bold">additive to the existing creator budget</span>, not a substitute for it. The brand keeps its roster and adds a line item — a shorter sales cycle and no rip-and-replace.
          </p>
          <p className="text-[10px] text-gray-500 mt-3 leading-snug">Stated as mechanism, not as a measured result.</p>
        </div>
      </div>
    </ContentShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// THE OPEN LANE — trade-desk landscape table (iter 86.3)
//
// Tells the founder's strongest macro story: every digital channel has a
// programmatic trade desk EXCEPT influencer. Maps user's 2025 ad-spend
// table to who currently owns each channel, then visually highlights the
// open lane WAVE is positioning to own.
// ───────────────────────────────────────────────────────────────────────
const TRADE_DESK_LANDSCAPE = [
  { channel: 'Search',                spend: '$114.2B', yoy: '+11.0%',  share: '38.8%', owner: 'Google · Bing',                       owned: true,  growth: 'stable' },
  { channel: 'Social',                spend: '$117.7B', yoy: '+32.6%',  share: '40.0%', owner: 'Meta · TikTok · YouTube self-serve',  owned: true,  growth: 'hot' },
  { channel: 'Display',               spend:  '$81.6B', yoy:  '+9.8%',  share: '27.7%', owner: 'The Trade Desk · DV360',              owned: true,  growth: 'stable' },
  { channel: 'Video (Digital)',       spend:  '$78.0B', yoy: '+25.4%',  share: '26.5%', owner: 'The Trade Desk · YouTube · Roku',     owned: true,  growth: 'hot' },
  { channel: 'OTT / CTV',             spend:  '$33.0B', yoy: '+16.0%',  share: '46% of video', owner: 'The Trade Desk · Magnite',     owned: true,  growth: 'hot' },
  { channel: 'Audio (Digital)',       spend:   '$8.4B', yoy: '+10.2%',  share:  '2.8%', owner: 'Spotify · iHeart · Acast',            owned: true,  growth: 'stable' },
  { channel: 'Influencer',            spend:  '$10.5B', yoy:    '—',    share:  '3.6%', owner: 'No trade desk · agency PMs + sheets', owned: false, growth: 'open',  isWave: true },
  { channel: 'Linear TV',             spend:  '$55.0B', yoy:  '−7.0%',  share: '62% of TV', owner: 'Direct sales · Mediaocean',       owned: true,  growth: 'declining' },
];

function OpenLaneSection() {
  // Collapsible drop-down per iter 86.4 — user wants it minimizable, positioned
  // between the dictionary entry and the 4 architecture cards, and titled
  // "The Market Opportunity". Default state: open (so the table is the first
  // thing a cold-share investor sees on land), but a single click collapses
  // it for repeat-visit hygiene.
  const [open, setOpen] = useState(true);
  return (
    <section
      data-testid="open-lane-section"
      className="rounded-xl relative overflow-hidden"
      style={{ background: CARD, border: '1px solid rgba(255,77,148,0.18)' }}
    >
      {/* clickable header */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="open-lane-toggle"
        aria-expanded={open}
        className="relative w-full flex items-center justify-between gap-4 px-6 lg:px-8 py-5 text-left transition-colors hover:bg-white/[0.02]"
      >
        {/* corner glow */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
             style={{ background: PINK }} />
        <div className="relative flex items-baseline gap-3 flex-wrap min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: PINK }}>
            The Market Opportunity
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 truncate">
            2025 · global digital ad spend · {open ? 'tap to minimize' : 'tap to expand'}
          </p>
        </div>
        <span
          className="relative flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full transition-transform duration-300"
          style={{
            background: `${PINK}14`,
            border: `1px solid ${PINK}44`,
            color: PINK,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {/* collapsible body */}
      {open && (
        <div className="px-6 lg:px-8 pb-6 lg:pb-8 relative"
             data-testid="open-lane-body"
             style={{ animation: 'mc-fade-in 320ms ease-out' }}>
          <h2 className="text-2xl lg:text-3xl font-black text-white tracking-tight leading-tight">
            Every digital channel has a trade desk.
            <span style={{ color: PINK }}> The creator channel doesn&apos;t.</span>
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed mt-3 max-w-3xl">
            WAVE is positioning to become <span className="text-white font-bold">the trade desk for micro-creator spend at scale</span> — the way The Trade Desk did for programmatic display + CTV. Creator marketing is the only major channel where spend is fragmented across agencies, spreadsheets, and DM threads. We&apos;re building the platform that lets a brand deploy a coordinated wave of micro-creators with the speed, measurability, and orchestration logic investors already trust in TTD.
          </p>

          {/* Table */}
          <div className="mt-6 rounded-xl overflow-hidden border border-white/5" data-testid="open-lane-table">
            {/* header */}
            <div className="grid grid-cols-[1.4fr_0.9fr_0.7fr_1.6fr_0.7fr] gap-3 px-4 py-3 bg-black/40 border-b border-white/5">
              <div className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500">Channel</div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500 text-right">2025 Spend</div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500 text-right">YoY</div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500">Trade desk owner</div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500 text-right">Status</div>
            </div>
            {/* rows */}
            {TRADE_DESK_LANDSCAPE.map(row => {
              const isWave = row.isWave;
              const growthColor =
                row.growth === 'hot'       ? '#4ade80' :
                row.growth === 'open'      ? PINK      :
                row.growth === 'declining' ? '#FF6B6B' : '#9ca3af';
              const statusLabel =
                row.growth === 'hot'       ? 'Compounding'   :
                row.growth === 'open'      ? 'Open lane'     :
                row.growth === 'declining' ? 'Bleeding'      : 'Stable';
              return (
                <div
                  key={row.channel}
                  data-testid={`landscape-row-${row.channel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="grid grid-cols-[1.4fr_0.9fr_0.7fr_1.6fr_0.7fr] gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 transition-colors"
                  style={{
                    background: isWave ? `${PINK}0e` : 'transparent',
                    boxShadow: isWave ? `inset 2px 0 0 ${PINK}` : 'none',
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <p className={`text-xs font-bold truncate ${isWave ? 'text-white' : 'text-gray-200'}`}>
                      {row.channel}
                    </p>
                    {isWave && (
                      <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider flex-shrink-0"
                            style={{ background: `${PINK}22`, color: PINK, border: `1px solid ${PINK}44` }}>
                        WAVE
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-bold text-white tabular-nums text-right">{row.spend}</div>
                  <div className="text-xs font-bold tabular-nums text-right" style={{ color: growthColor }}>
                    {row.yoy}
                  </div>
                  <div className={`text-[11px] truncate ${isWave ? 'text-white font-bold' : 'text-gray-400'}`}>
                    {row.owner}
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] uppercase tracking-wider font-black"
                          style={{ color: growthColor }}>
                      {statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* The math callout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-6" data-testid="open-lane-math-strip">
            <div className="rounded-xl p-4 bg-black/40 border border-white/5">
              <p className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500 mb-1">Today</p>
              <p className="text-2xl font-black text-white tabular-nums">$10.5B</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Creator-channel global spend (reported as “influencer”) — the fragmented base</p>
            </div>
            <div className="rounded-xl p-4 bg-black/40 border border-white/5">
              <p className="text-[9px] uppercase tracking-[0.22em] font-black text-gray-500 mb-1">Compounding adjacency</p>
              <p className="text-2xl font-black tabular-nums" style={{ color: '#4ade80' }}>$195B</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Social ($117.7B · +32.6%) + Digital Video ($78B · +25.4%) — the creator channel&apos;s gravitational pool</p>
            </div>
            <div className="rounded-xl p-4 relative overflow-hidden"
                 style={{ background: `${PINK}0e`, border: `1px solid ${PINK}33` }} data-testid="illustrative-ceiling">
              <p className="text-[9px] uppercase tracking-[0.22em] font-black mb-1" style={{ color: PINK }}>Illustrative ceiling</p>
              <p className="text-2xl font-black tabular-nums" style={{ color: PINK }}>$157M</p>
              <p className="text-[11px] text-gray-300 mt-0.5 tabular-nums">
                10% share × $10.5B spend × 15% platform fee.
              </p>
              <p className="text-[10px] text-gray-500 mt-1">An illustration of scale at those three assumptions — not a forecast.</p>
            </div>
          </div>

          {/* Closing strike */}
          <div className="mt-5 rounded-lg p-4 bg-black/40 border-l-2"
               style={{ borderLeftColor: PINK, borderLeftWidth: '2px' }}>
            <p className="text-sm text-white leading-snug">
              <span className="font-black" style={{ color: PINK }}>The takeaway:</span>{' '}
              $360B+ of digital ad spend already routes through programmatic trade desks. Creator marketing is the last major channel without one — and the fastest-growing adjacent channel (social, <span className="text-white font-bold">+32.6% YoY</span>) is funneling spend toward it. <span className="text-white font-bold">WAVE is the trade desk for that lane.</span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Closing
// ───────────────────────────────────────────────────────────────────────
function ClosingBlock() {
  return (
    <section data-testid="closing-block" className="rounded-xl p-8 lg:p-10 relative overflow-hidden text-center"
             style={{ background: CARD, border: '1px solid rgba(0,212,255,0.18)' }}>
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-15 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${TEAL}, transparent 60%)` }} />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.28em] font-black mb-3" style={{ color: TEAL }}>
          The Future of Creator Advertising
        </p>
        <p className="text-xl lg:text-2xl font-bold text-white leading-snug max-w-3xl mx-auto">
          WAVE doesn&apos;t just connect brands and creators —
          <br className="hidden sm:block" />
          <span className="text-white"> it owns the </span>
          <span style={{ color: TEAL }} className="font-black">closed-loop intelligence layer</span>
          <span className="text-white"> that makes the entire ecosystem smarter every day.</span>
        </p>
        <p className="text-sm text-gray-400 mt-3 max-w-3xl mx-auto">
          This is the future of creator advertising.
        </p>
        <p className="text-[11px] text-gray-500 mt-7 max-w-2xl mx-auto leading-relaxed">
          Live walkthrough of the full closed-loop platform — DSP, SSP, Digital DNA, and Burst Orchestration — is available on request.
        </p>
        <a
          href="mailto:founders@wavedsp.ai?subject=WAVE%20AI%20%C2%B7%20Investor%20walkthrough%20request"
          data-testid="closing-walkthrough-cta"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all hover:brightness-110"
          style={{ background: 'transparent', color: TEAL, border: `1px solid ${TEAL}55` }}
        >
          <Mail className="h-3.5 w-3.5" />
          Request investor walkthrough
        </a>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ───────────────────────────────────────────────────────────────────────
export default function InvestorOverviewPage() {
  // Default: WAVE Deployment Tech (the breakthrough) — per user spec.
  const [active, setActive] = useState('tech');

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const ContentComponent = {
    tech: TechContent,
    dsp:  DspContent,
    ssp:  SspContent,
    dna:  DnaContent,
    cmd:  CmdContent,
  }[active] || TechContent;

  return (
    <div style={{ background: BG, color: '#fff', minHeight: '100vh', fontFamily: 'Inter, ui-sans-serif, system-ui' }}
         data-testid="investor-overview-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur" style={{ background: 'rgba(10,10,10,0.78)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WaveLogoMark size={24} animated />
            <span className="text-sm font-black tracking-tight text-white">WAVE AI</span>
          </div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider" data-testid="overview-date">{today}</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16 space-y-12 sm:space-y-14 lg:space-y-16">
        {/* ═ Hero thesis ═ */}
        <section className="text-center max-w-4xl mx-auto" data-testid="hero-thesis">
          <p className="text-[10px] uppercase tracking-[0.32em] font-black mb-4" style={{ color: PINK }}>
            Closed Loop · DSP × SSP × Digital DNA × CMD × Deployment Tech
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-[1.05] text-white">
            <span className="block">WAVE AI —</span>
            <span className="block mt-1">
              Closed Loop Platform that <span style={{ color: TEAL }}>deploys</span>
            </span>
            <span className="block mt-1">
              everyday micro-creators into a <span style={{ color: PINK }}>high-impact media channel</span>.
            </span>
          </h1>
        </section>

        {/* ═ What is a Wave? (kept — already a 10/10 component) ═ */}
        <WaveDictionaryEntry />

        {/* ═ THE MARKET OPPORTUNITY · collapsible (iter 86.4) ═ */}
        <OpenLaneSection />

        {/* ═ Core Architecture · Already Built & Live ═ */}
        <section data-testid="architecture-section">
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-[10px] uppercase tracking-[0.32em] font-black mb-2" style={{ color: TEAL }}>
              Core Architecture
            </p>
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Already Built &amp; Live
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-xl mx-auto">
              Demand, intelligence, and supply — one closed-loop platform. Click any card to drill in.
            </p>
          </div>
          {/* Three-tablet architecture row: DSP | Targeting & Intelligence | SSP.
              The middle tablet is a group container holding two stacked
              sub-product tiles (Digital DNA + Wave Deployment Tech) that
              remain individually clickable to drill into their content. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch" data-testid="pillar-cards-row">
            <PillarCard
              pillar={PILLARS.find(p => p.key === 'dsp')}
              active={active === 'dsp'}
              onSelect={setActive}
            />
            <MiddleTabletGroup
              pillars={PILLARS.filter(p => p.key === 'dna' || p.key === 'cmd' || p.key === 'tech')}
              active={active}
              onSelect={setActive}
            />
            <PillarCard
              pillar={PILLARS.find(p => p.key === 'ssp')}
              active={active === 'ssp'}
              onSelect={setActive}
            />
          </div>
        </section>

        {/* ═ Dynamic content area ═ */}
        <div data-testid="pillar-content-area">
          <ContentComponent />
        </div>

        {/* ═ Complementary, not competitive ═ */}
        <ComplementBox />

        {/* ═ Closing ═ */}
        <ClosingBlock />

        {/* ═ Founder footer (kept thin) ═ */}
        <footer className="border-t border-white/5 pt-6 pb-12 text-center" data-testid="founder-footer">
          <div className="flex items-center justify-center gap-2 mb-2">
            <WaveLogoMark size={18} />
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-gray-500">WAVE AI · Investor Overview</p>
          </div>
          <p className="text-xs text-gray-500">
            For deeper questions or to schedule the live demo —
            <a href="mailto:founders@wavedsp.ai" className="text-gray-300 hover:text-white inline-flex items-center gap-1 ml-1 transition-colors">
              <Mail className="h-3 w-3" />founders@wavedsp.ai
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
